import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import de from './locales/de.json';
import en from './locales/en.json';
import fr from './locales/fr.json';

/**
 * i18n SmartLawyer
 *
 * - Pages publiques : ressources locales fr / en / de (namespace `translation`).
 * - Mode legalchat : Chainlit ajoute ensuite son propre bundle (ex: `fr-FR`)
 *   via AppWrapper → i18n.addResourceBundle. Les clés publiques absentes de
 *   ce bundle retombent sur `fr` / `en` / `de` grâce au fallback i18next.
 *
 * La langue par défaut de l'instance est injectée au runtime par le serveur
 * (`window.__DEFAULT_LANGUAGE__`, env DEFAULT_LANGUAGE) — `en` sur
 * dev.smartlawyer.ai, `fr` sinon.
 */

export const SUPPORTED_LANGUAGES = ['fr', 'en', 'de'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

function isSupported(lng: string): lng is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(lng);
}

export function getDefaultLanguage(): SupportedLanguage {
  const lng = (window.__DEFAULT_LANGUAGE__ || 'fr').slice(0, 2).toLowerCase();
  return isSupported(lng) ? lng : 'fr';
}

/** Langue courante ramenée à fr / en / de (ex: `fr-FR` → `fr`). */
export function getCurrentLanguage(): SupportedLanguage {
  const lng = (i18n.resolvedLanguage || i18n.language || '')
    .slice(0, 2)
    .toLowerCase();
  return isSupported(lng) ? lng : getDefaultLanguage();
}

export function i18nSetupLocalization(): void {
  const defaultLng = getDefaultLanguage();

  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        fr: { translation: fr },
        en: { translation: en },
        de: { translation: de }
      },
      defaultNS: 'translation',
      fallbackLng: Array.from(new Set([defaultLng, 'fr'])),
      detection: {
        // ?lang=de (liens hreflang) > choix mémorisé > navigateur > <html lang>
        order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
        lookupQuerystring: 'lang',
        caches: ['localStorage'],
        // Normalise en-US → en ; langue non gérée → langue par défaut
        convertDetectedLanguage: (lng: string) => {
          const base = lng.slice(0, 2).toLowerCase();
          return isSupported(base) ? base : defaultLng;
        }
      },
      interpolation: { escapeValue: false }
    })
    .catch((err) => console.error('[i18n] Failed to setup localization.', err));

  const syncHtmlLang = (lng: string) => {
    document.documentElement.lang = lng.slice(0, 2);
  };
  syncHtmlLang(i18n.language || defaultLng);
  i18n.on('languageChanged', syncHtmlLang);
}

export default i18n;

const DATE_LOCALES: Record<SupportedLanguage, string> = {
  fr: 'fr-FR',
  en: 'en-US',
  de: 'de-DE'
};

/** Locale BCP 47 pour toLocaleDateString / Intl.NumberFormat (ex: 'en-US'). */
export function getDateLocale(): string {
  return DATE_LOCALES[getCurrentLanguage()];
}
